import type { SelectWorkoutLog } from "@/api/db/schema/workout-logs";
import { createId } from "@paralleldrive/cuid2";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { getLogsForDayOptions } from "./queries";
import { upsertWorkoutSetFn } from "./server";

type WorkoutLogMutationData = {
  programId: string;
  dayId: string;
  exerciseId: string;
  exerciseOrder: number;
  setNumber: number;
  completedWeight?: number | null;
  completedReps?: number | null;
  completedRPE?: number | null;
  notes?: string | null;
  videoUrl?: string | null;
  videoThumbnailUrl?: string | null;
  videoTrimStart?: number | null;
  videoTrimEnd?: number | null;
  videoProcessingStatus?: "pending" | "processing" | "done" | "error" | null;
};

type WorkoutDayLog = SelectWorkoutLog & {
  weekNumber: number | null;
  dayNumber: number | null;
  dayName: string | null;
};

type LogsQueryData = {
  status: 200;
  body: {
    logs: WorkoutDayLog[];
    total: number;
  };
};

function getLogsQueryKey(programId: string, dayId: string) {
  return getLogsForDayOptions(programId, dayId).queryKey;
}

const MUTATION_KEY = ["workoutLog"] as const;

export function useWorkoutLogMutation() {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationKey: MUTATION_KEY,
    mutationFn: (data: WorkoutLogMutationData) => upsertWorkoutSetFn({ data }),

    onMutate: async (data) => {
      const queryKey = getLogsQueryKey(data.programId, data.dayId);

      // Cancel any in-flight refetches so they don't overwrite optimistic update
      await queryClient.cancelQueries({ queryKey });

      // Snapshot current data for rollback
      const previousData = queryClient.getQueryData<LogsQueryData>(queryKey);

      // Optimistically update cache
      queryClient.setQueryData<LogsQueryData>(queryKey, (old) => {
        if (!old || old.status !== 200) return old;

        const logs = old.body.logs ?? [];

        // Find existing log by composite key
        const existingIndex = logs.findIndex(
          (log) =>
            log.exerciseId === data.exerciseId &&
            log.exerciseOrder === data.exerciseOrder &&
            log.setNumber === data.setNumber,
        );

        let updatedLogs: WorkoutDayLog[];

        if (existingIndex >= 0) {
          // Update existing log — only update provided fields
          updatedLogs = logs.map((log, i) =>
            i === existingIndex
              ? {
                  ...log,
                  completedWeight:
                    data.completedWeight !== undefined
                      ? data.completedWeight
                      : log.completedWeight,
                  completedReps:
                    data.completedReps !== undefined
                      ? data.completedReps
                      : log.completedReps,
                  completedRPE:
                    data.completedRPE !== undefined
                      ? data.completedRPE
                      : log.completedRPE,
                  notes: data.notes !== undefined ? data.notes : log.notes,
                  videoUrl:
                    data.videoUrl !== undefined ? data.videoUrl : log.videoUrl,
                  videoThumbnailUrl:
                    data.videoThumbnailUrl !== undefined
                      ? data.videoThumbnailUrl
                      : log.videoThumbnailUrl,
                  videoTrimStart:
                    data.videoTrimStart !== undefined
                      ? data.videoTrimStart
                      : log.videoTrimStart,
                  videoTrimEnd:
                    data.videoTrimEnd !== undefined
                      ? data.videoTrimEnd
                      : log.videoTrimEnd,
                  videoProcessingStatus:
                    data.videoProcessingStatus !== undefined
                      ? data.videoProcessingStatus
                      : log.videoProcessingStatus,
                  updatedAt: new Date(),
                }
              : log,
          );
        } else {
          // Insert new log optimistically with a temp ID
          const newLog: WorkoutDayLog = {
            logId: `optimistic-${createId()}`,
            userId: "optimistic",
            programId: data.programId,
            dayId: data.dayId,
            exerciseId: data.exerciseId,
            exerciseOrder: data.exerciseOrder,
            setNumber: data.setNumber,
            completedWeight: data.completedWeight ?? null,
            completedReps: data.completedReps ?? null,
            completedRPE: data.completedRPE ?? null,
            topSetLiftType: null,
            notes: data.notes ?? null,
            videoUrl: data.videoUrl ?? null,
            videoThumbnailUrl: data.videoThumbnailUrl ?? null,
            videoTrimStart: data.videoTrimStart ?? null,
            videoTrimEnd: data.videoTrimEnd ?? null,
            videoProcessingStatus: data.videoProcessingStatus ?? null,
            loggedAt: new Date(),
            createdAt: new Date(),
            updatedAt: new Date(),
            weekNumber: null,
            dayNumber: null,
            dayName: null,
          };
          updatedLogs = [...logs, newLog];
        }

        return {
          ...old,
          body: {
            ...old.body,
            logs: updatedLogs,
            total: updatedLogs.length,
          },
        };
      });

      return { previousData, queryKey };
    },

    onSuccess: (result, variables) => {
      if (result?.status === 200 && result.body && "log" in result.body) {
        const queryKey = getLogsQueryKey(variables.programId, variables.dayId);
        const savedLog = {
          ...(result.body.log as SelectWorkoutLog),
          weekNumber: null,
          dayNumber: null,
          dayName: null,
        } as WorkoutDayLog;

        queryClient.setQueryData<LogsQueryData>(queryKey, (old) => {
          // If cache is uninitialized (e.g. first mutation before query resolves),
          // bootstrap it with just the saved log.
          const currentLogs = old?.status === 200 ? (old.body.logs ?? []) : [];

          const existingIndex = currentLogs.findIndex(
            (log) =>
              log.exerciseId === variables.exerciseId &&
              log.exerciseOrder === variables.exerciseOrder &&
              log.setNumber === variables.setNumber,
          );

          const updatedLogs =
            existingIndex >= 0
              ? currentLogs.map((log, i) =>
                  i === existingIndex ? savedLog : log,
                )
              : [...currentLogs, savedLog];

          return {
            status: 200,
            body: {
              logs: updatedLogs,
              total: updatedLogs.length,
            },
          };
        });
        queryClient.invalidateQueries({ queryKey: ["topSetProgress"] });
      }
    },

    onError: (_err, _data, context) => {
      // Roll back to the snapshot
      if (context?.previousData !== undefined) {
        queryClient.setQueryData(context.queryKey, context.previousData);
      }
      // Invalidate ile server'dan temiz data gelsin
      const queryKey = getLogsQueryKey(_data.programId, _data.dayId);
      queryClient.invalidateQueries({ queryKey });
      toast.error("Kaydetme hatası. Lütfen tekrar deneyin.");
    },

    onSettled: (_result, error, data) => {
      // Sadece hata durumunda invalidate et — başarılı mutation'larda
      // onSuccess zaten server data'sını cache'e yazar
      if (error) {
        const queryKey = getLogsQueryKey(data.programId, data.dayId);
        queryClient.invalidateQueries({ queryKey });
      }
    },
  });

  return {
    mutate: mutation.mutate,
    mutateAsync: mutation.mutateAsync,
    isPending: mutation.isPending,
  };
}
